import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (account: string, password: string) =>
    api.post('/api/auth/login', { account, password }),
  register: (data: { account: string; password: string; name: string; phone?: string; role?: string }) =>
    api.post('/api/auth/register', data),
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { oldPassword, newPassword }),
  getMe: () => api.get('/api/auth/me'),
};

// User APIs
export const userApi = {
  getAll: () => api.get('/api/users'),
  getById: (id: number) => api.get(`/api/users/${id}`),
  update: (id: number, data: Partial<{ name: string; phone: string; birthDate: string; idNumber: string }>) =>
    api.put(`/api/users/${id}`, data),
  toggleStatus: (id: number) => api.patch(`/api/users/${id}/toggle-status`),
};

// Account APIs
export const accountApi = {
  getRecords: () => api.get('/api/accounts/records'),
  getLedger: (params?: { limit?: number; offset?: number }) =>
    api.get('/api/accounts/ledger', { params }),
  getMsLedger: (params?: { limit?: number; offset?: number }) =>
    api.get('/api/accounts/ms-ledger', { params }),
  getMsLimit: () => api.get('/api/accounts/ms-limit'),
};

// Transfer APIs
export const transferApi = {
  transferNT: (data: { receiverAccount: string; amount: number; reason?: string; password: string }) =>
    api.post('/api/transfers/nt', data),
  transferMsFractional: (data: { receiverAccount: string; amountNT: number; reason?: string; password: string }) =>
    api.post('/api/transfers/ms-fractional', data),
  transferMsDirect: (data: { receiverAccount: string; msAmount: string; reason?: string; password: string }) =>
    api.post('/api/transfers/ms-direct', data),
};

// Exchange APIs
export const exchangeApi = {
  toNT: (data: { amount: number; password: string }) =>
    api.post('/api/exchange/to-nt', data),
  toMS: (data: { amount: number; password: string }) =>
    api.post('/api/exchange/to-ms', data),
  getRate: () => api.get('/api/exchange/rate'),
};

// Grade APIs
export const gradeApi = {
  getActive: () => api.get('/api/grades/active'),
  submit: (data: { registrationId: number; score: number }) =>
    api.post('/api/grades/submit', data),
  getAll: () => api.get('/api/grades/all'),
  getRegistrations: () => api.get('/api/grades/all'),
  create: (data: { subject: string; testName: string; testDate?: string; maxScore: number; startTime: string; endTime: string }) =>
    api.post('/api/grades/create', data),
  createRegistration: (name: string) =>
    api.post('/api/grades/create', { subject: name, testName: name, maxScore: 100, startTime: new Date().toISOString(), endTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() }),
  getGrades: (id: number) => api.get(`/api/grades/${id}/grades`),
};

// Fee APIs
export const feeApi = {
  getMyFees: () => api.get('/api/fees/my-fees'),
  getAll: () => api.get('/api/fees/all'),
  create: (data: { userId: number; feeType: string; amount: number; semester?: string }) =>
    api.post('/api/fees/create', data),
  pay: (id: number) => api.patch(`/api/fees/${id}/pay`),
  getRequests: () => api.get('/api/fees/requests'),
  createRequest: (data: { amount: number; reason: string }) =>
    api.post('/api/fees/requests', data),
  updateRequest: (id: number, data: { status: 'approved' | 'rejected' }) =>
    api.patch(`/api/fees/requests/${id}`, data),
};

// Shop APIs
export const shopApi = {
  getCategories: () => api.get('/api/shop/categories'),
  getItems: (categoryId?: number) =>
    api.get('/api/shop/items', { params: categoryId ? { categoryId } : {} }),
  createOrder: (data: { items: { itemId: number; quantity: number }[] }) =>
    api.post('/api/shop/orders', data),
  getOrders: () => api.get('/api/shop/orders'),
  adminCreateCategory: (data: { name: string; description?: string; sortOrder?: number }) =>
    api.post('/api/shop/admin/categories', data),
  adminCreateItem: (data: { categoryId: number; name: string; price: number; stock: number; description?: string }) =>
    api.post('/api/shop/admin/items', data),
};

// Announcement APIs
export const announcementApi = {
  getActive: () => api.get('/api/announcements'),
  getAll: (params?: { page?: number; limit?: number }) =>
    api.get('/api/announcements/all', { params }),
  getById: (id: number) => api.get(`/api/announcements/${id}`),
  create: (data: { title: string; content: string; priority?: number; isPinned?: boolean; startDate?: string; endDate?: string }) =>
    api.post('/api/announcements', data),
  update: (id: number, data: Partial<{ title: string; content: string; priority: number; isPinned: boolean; startDate: string; endDate: string }>) =>
    api.put(`/api/announcements/${id}`, data),
  delete: (id: number) => api.delete(`/api/announcements/${id}`),
};

// Notification APIs
export const notificationApi = {
  getAll: (params?: { unreadOnly?: boolean; limit?: number }) =>
    api.get('/api/notifications', { params }),
  markRead: (id: number) => api.patch(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/read-all'),
  delete: (id: number) => api.delete(`/api/notifications/${id}`),
};

// Admin APIs
export const adminApi = {
  getStats: () => api.get('/api/admin/stats'),
  getUsers: (params?: { page?: number; limit?: number }) =>
    api.get('/api/admin/users', { params }),
  createUser: (data: { account: string; password: string; name: string; phone?: string; role?: string; initialNT?: number; initialMS?: string; dailyLimit?: number }) =>
    api.post('/api/admin/users', data),
  updateUser: (id: number, data: Partial<{ name: string; phone: string; role: string; ntBalance: number; msBalance: string; isActive: boolean; dailyLimit: number; resetPassword: string }>) =>
    api.put(`/api/admin/users/${id}`, data),
  bulkCreateUsers: (users: { account: string; password: string; name: string; role?: string }[]) =>
    api.post('/api/admin/users/bulk', { users }),
  getLogs: (params?: { limit?: number; type?: string }) =>
    api.get('/api/admin/logs', { params }),
};
